/**
 * Schedule Exception Form Component
 * Add/edit schedule exceptions (time off, special hours)
 */

import { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, Button, Input } from '../ui';
import { Plus, Calendar, Clock, Trash2 } from 'lucide-react';
import type { ScheduleException } from '../../types';
import { useCan } from '../../contexts/useAuth';

interface ScheduleExceptionFormProps {
  exceptions: ScheduleException[];
  onAddException: (exception: ScheduleException) => Promise<void>;
  onRemoveException: (date: string) => Promise<void>;
  isLoading?: boolean;
}

export default function ScheduleExceptionForm({
  exceptions,
  onAddException,
  onRemoveException,
  isLoading,
}: ScheduleExceptionFormProps) {
  const can = useCan();
  const [isAdding, setIsAdding] = useState(false);
  const [newException, setNewException] = useState<ScheduleException>({
    date: '',
    start: '',
    end: '',
    reason: '',
  });
  const [removingDate, setRemovingDate] = useState<string | null>(null);

  const handleAdd = async () => {
    if (!newException.date) return;
    
    await onAddException(newException);
    setNewException({ date: '', start: '', end: '', reason: '' });
    setIsAdding(false);
  };

  const handleRemove = async (date: string) => {
    setRemovingDate(date);
    try {
      await onRemoveException(date);
    } finally {
      setRemovingDate(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  // Sort exceptions by date
  const sortedExceptions = [...exceptions].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );

  // Filter to show only future exceptions
  const futureExceptions = sortedExceptions.filter(
    (e) => new Date(e.date) >= new Date(new Date().toDateString())
  );

  return (
    <Card padding="none">
      <CardHeader className="px-4 pt-4 pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Schedule Exceptions</CardTitle>
          {!isAdding && can('schedules.manage') && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsAdding(true)}
              leftIcon={<Plus className="h-4 w-4" />}
            >
              Add
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {/* Add Exception Form */}
        {isAdding && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Date
                </label>
                <Input
                  type="date"
                  value={newException.date}
                  onChange={(e) =>
                    setNewException({ ...newException, date: e.target.value })
                  }
                  size="sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Reason
                </label>
                <Input
                  placeholder="e.g., Vacation, Sick leave"
                  value={newException.reason || ''}
                  onChange={(e) =>
                    setNewException({ ...newException, reason: e.target.value })
                  }
                  size="sm"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Start Time (optional)
                </label>
                <Input
                  type="time"
                  value={newException.start || ''}
                  onChange={(e) =>
                    setNewException({ ...newException, start: e.target.value })
                  }
                  size="sm"
                  placeholder="Leave empty for day off"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  End Time (optional)
                </label>
                <Input
                  type="time"
                  value={newException.end || ''}
                  onChange={(e) =>
                    setNewException({ ...newException, end: e.target.value })
                  }
                  size="sm"
                  placeholder="Leave empty for day off"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Leave times empty for a full day off, or specify times for modified hours.
            </p>
            <div className="flex gap-2">
              {can('schedules.manage') && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleAdd}
                  loading={isLoading}
                  disabled={!newException.date}
                >
                  Add Exception
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setIsAdding(false);
                  setNewException({ date: '', start: '', end: '', reason: '' });
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Exceptions List */}
        {futureExceptions.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <Calendar className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No upcoming exceptions</p>
          </div>
        ) : (
          <div className="space-y-2">
            {futureExceptions.map((exception) => (
              <div
                key={exception.date}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white rounded-lg border border-gray-200">
                    <Calendar className="h-4 w-4 text-gray-600" />
                  </div>
                  <div>
                    <p className="font-medium text-sm text-gray-900">
                      {formatDate(exception.date)}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      {exception.start && exception.end ? (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {exception.start} - {exception.end}
                        </span>
                      ) : (
                        <span className="text-red-500">Day off</span>
                      )}
                      {exception.reason && (
                        <span>• {exception.reason}</span>
                      )}
                    </div>
                  </div>
                </div>
                {can('schedules.manage') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRemove(exception.date)}
                    loading={removingDate === exception.date}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
